import React, { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "cs_pwa_dismissed_at";
const DISMISS_DAYS = 14;

function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

function isInStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function recentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    if (isInStandalone() || recentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS doesn't fire beforeinstallprompt — show our own hint after a moment
    if (isIos()) {
      const t = setTimeout(() => setShowIos(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      setShow(false);
    }
    setDeferred(null);
  };

  const dismiss = (which) => {
    try {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
    } catch {}
    if (which === "ios") setShowIos(false);
    else setShow(false);
  };

  // Android / desktop Chrome install banner
  if (show) {
    return (
      <div
        className="fixed bottom-20 md:bottom-6 right-4 left-4 md:left-auto md:w-80 z-40 brut-card p-4 bg-[var(--cs-money)] pop-in"
        data-testid="pwa-install-prompt"
      >
        <div className="flex items-start gap-3">
          <div className="brut-card bg-white p-1.5 shrink-0">
            <Download size={20} />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg font-extrabold leading-tight">Install CashStreak</div>
            <p className="text-xs font-semibold mt-1">Add it to your home screen for one-tap access + streak notifications.</p>
            <div className="mt-3 flex gap-2">
              <button onClick={install} className="brut-btn brut-btn-ink !py-2 !px-3 text-xs" data-testid="pwa-install-yes">
                Install
              </button>
              <button onClick={() => dismiss()} className="brut-btn !py-2 !px-3 text-xs bg-white" data-testid="pwa-install-no">
                Not now
              </button>
            </div>
          </div>
          <button onClick={() => dismiss()} className="text-[var(--cs-ink)] -mt-1 -mr-1 p-1" data-testid="pwa-install-close">
            <X size={18} />
          </button>
        </div>
      </div>
    );
  }

  // iOS Safari instructions
  if (showIos) {
    return (
      <div
        className="fixed bottom-20 md:bottom-6 right-4 left-4 md:left-auto md:w-80 z-40 brut-card p-4 bg-[var(--cs-yellow)] pop-in"
        data-testid="pwa-ios-prompt"
      >
        <div className="flex items-start gap-3">
          <div className="brut-card bg-white p-1.5 shrink-0">
            <Share size={20} />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg font-extrabold leading-tight">Add to Home Screen</div>
            <p className="text-xs font-semibold mt-1">
              Tap <Share size={11} className="inline mb-0.5" /> at the bottom of Safari, then{" "}
              <span className="inline-flex items-center gap-0.5 font-bold">
                <Plus size={11} /> Add to Home Screen
              </span>
              .
            </p>
            <button onClick={() => dismiss("ios")} className="brut-btn !py-2 !px-3 text-xs bg-white mt-3" data-testid="pwa-ios-close">
              Got it
            </button>
          </div>
          <button onClick={() => dismiss("ios")} className="text-[var(--cs-ink)] -mt-1 -mr-1 p-1">
            <X size={18} />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
